import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { BrandHomePage } from './brandHome/BrandHomePage';
import { StoreHomePage } from './storeHome/StoreHomePage';
import { AdminHomePage } from './adminHome/AdminHomePage';

/**
 * `/app/dashboard` renders one of three very different pages depending on
 * account type. Each page fires its own role-scoped queries, so deciding
 * which component mounts here — before any of their hooks run — keeps a
 * store or admin session from ever touching a vendor's queries and vice
 * versa.
 *
 * `getEffectiveUserType(null)` resolves to `'store'` — a genuine default for
 * a real store user, but wrong for a vendor whose profile just hasn't
 * finished loading yet. Wait for auth to settle (and for the profile itself,
 * unless impersonation already tells us the role) before picking a page, so
 * a vendor's first paint can't briefly mount the wrong page and fire the
 * queries this gate exists to avoid.
 *
 * Admin is checked before store: an admin's own profile can carry
 * `userType: 'store'`, so `effectiveRole === 'admin'` must win the branch.
 */
export const DashboardGate: React.FC = () => {
  const { userProfile, loading } = useAuth();
  const { getEffectiveRole, getEffectiveUserType, impersonatedRole } = useImpersonation();

  if (loading) return null;
  if (!userProfile && impersonatedRole !== 'vendor') return null;

  if (getEffectiveUserType(userProfile) === 'vendor_supplier') {
    return <BrandHomePage />;
  }

  if (getEffectiveRole(userProfile) === 'admin') {
    return <AdminHomePage />;
  }

  return <StoreHomePage />;
};
